import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n, LangSwitch } from "@/lib/i18n";
import { Fingerprint, ArrowRight, Key } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError(t("reset_pw_min")); return; }
    if (newPassword !== confirm) { setError(t("reset_pw_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, new_password: newPassword });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#F4F4F0]">
      <div className="hidden md:flex relative bg-black text-white flex-col p-10 justify-between overflow-hidden">
        <div className="relative">
          <div className="flex items-center gap-3">
            <Fingerprint size={36} weight="bold" />
            <div className="font-display font-black text-3xl tracking-tighter">ATTENDIO</div>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mt-2 opacity-80">{t("brand_line")}</div>
        </div>
        <div className="relative">
          <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter leading-[0.9]">
            {t("reset_pw_title")}
          </div>
        </div>
        <div />
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="reset-form" className="w-full max-w-md brut-border bg-white p-8 brut-shadow-lg">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="font-display font-black text-4xl tracking-tighter flex items-center gap-2">
                <Key size={28} weight="bold" /> {t("reset_pw_title")}
              </div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mt-1">
                {t("reset_pw_hint")}
              </div>
            </div>
            <LangSwitch />
          </div>

          {!token && (
            <div data-testid="reset-no-token" className="brut-border bg-[#FF3333] text-white p-3 text-sm font-bold mb-2">
              {t("reset_pw_no_token")}
            </div>
          )}

          {done ? (
            <div data-testid="reset-success" className="brut-border bg-[#00CC33] text-black p-4 font-bold">
              {t("reset_pw_success")}
            </div>
          ) : (
            <>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("reset_pw_new")}</label>
              <input
                data-testid="reset-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full brut-border p-3 mb-4 focus:outline-none focus:border-[#0047FF] focus:shadow-[4px_4px_0_0_#0047FF] transition-all"
                required
                minLength={8}
                disabled={!token}
              />
              <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("reset_pw_confirm")}</label>
              <input
                data-testid="reset-confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full brut-border p-3 mb-2 focus:outline-none focus:border-[#0047FF] focus:shadow-[4px_4px_0_0_#0047FF] transition-all"
                required
                minLength={8}
                disabled={!token}
              />
              {error && (
                <div data-testid="reset-error" className="brut-border bg-[#FF3333] text-white p-2 text-sm font-bold mb-2">
                  {error}
                </div>
              )}
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading || !token}
                className="w-full mt-4 bg-black text-white brut-border brut-shadow brut-hover p-4 font-display font-black text-xl tracking-tighter uppercase flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "…" : t("reset_pw_submit")} <ArrowRight size={20} weight="bold" />
              </button>
            </>
          )}

          <div className="mt-6 text-xs">
            <Link to="/login" data-testid="back-to-login" className="underline font-bold">← {t("back_to_login")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
